export const COMMUNITY_IMPORT_CSV_MAX_ROWS = 500;
export const COMMUNITY_IMPORT_CSV_MAX_BYTES = 512_000;

export type CommunityImportJobKind = 'members' | 'posts';

export type CommunityImportCsvRowError = {
  row: number;
  message: string;
};

export type CommunityImportMemberRow = {
  email: string;
  username?: string | null;
  displayName?: string | null;
};

export type CommunityImportPostRow = {
  authorEmail: string;
  content: string;
  createdAt?: string | null;
};

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
}

export function parseCommunityImportCsv(content: string): string[][] {
  return content
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(parseCsvLine);
}

function headerIndex(headers: string[], names: string[]): number {
  const normalized = headers.map((header) => header.trim().toLowerCase());
  for (const name of names) {
    const index = normalized.indexOf(name.toLowerCase());
    if (index >= 0) return index;
  }
  return -1;
}

export function validateCommunityImportMemberCsv(content: string): {
  ok: true;
  rows: CommunityImportMemberRow[];
  errors: CommunityImportCsvRowError[];
} | {
  ok: false;
  message: string;
} {
  if (content.length > COMMUNITY_IMPORT_CSV_MAX_BYTES) {
    return { ok: false, message: 'CSV file is too large' };
  }

  const parsed = parseCommunityImportCsv(content);
  if (!parsed.length) return { ok: false, message: 'CSV is empty' };
  if (parsed.length - 1 > COMMUNITY_IMPORT_CSV_MAX_ROWS) {
    return { ok: false, message: `Maximum ${COMMUNITY_IMPORT_CSV_MAX_ROWS} data rows allowed` };
  }

  const headers = parsed[0] ?? [];
  const emailIndex = headerIndex(headers, ['email']);
  if (emailIndex < 0) return { ok: false, message: 'CSV must include an email column' };

  const usernameIndex = headerIndex(headers, ['username']);
  const displayNameIndex = headerIndex(headers, ['display_name', 'displayname', 'name']);

  const rows: CommunityImportMemberRow[] = [];
  const errors: CommunityImportCsvRowError[] = [];

  for (let i = 1; i < parsed.length; i += 1) {
    const line = parsed[i] ?? [];
    const email = (line[emailIndex] ?? '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      errors.push({ row: i + 1, message: 'Valid email is required' });
      continue;
    }
    rows.push({
      email,
      username: usernameIndex >= 0 ? line[usernameIndex]?.trim() || null : null,
      displayName: displayNameIndex >= 0 ? line[displayNameIndex]?.trim() || null : null,
    });
  }

  return { ok: true, rows, errors };
}

export function validateCommunityImportPostCsv(content: string): {
  ok: true;
  rows: CommunityImportPostRow[];
  errors: CommunityImportCsvRowError[];
} | {
  ok: false;
  message: string;
} {
  if (content.length > COMMUNITY_IMPORT_CSV_MAX_BYTES) {
    return { ok: false, message: 'CSV file is too large' };
  }

  const parsed = parseCommunityImportCsv(content);
  if (!parsed.length) return { ok: false, message: 'CSV is empty' };
  if (parsed.length - 1 > COMMUNITY_IMPORT_CSV_MAX_ROWS) {
    return { ok: false, message: `Maximum ${COMMUNITY_IMPORT_CSV_MAX_ROWS} data rows allowed` };
  }

  const headers = parsed[0] ?? [];
  const authorIndex = headerIndex(headers, ['author_email', 'email', 'author']);
  const contentIndex = headerIndex(headers, ['content', 'body', 'text']);
  const createdAtIndex = headerIndex(headers, ['created_at', 'createdat', 'date']);

  if (authorIndex < 0 || contentIndex < 0) {
    return { ok: false, message: 'CSV must include author_email and content columns' };
  }

  const rows: CommunityImportPostRow[] = [];
  const errors: CommunityImportCsvRowError[] = [];

  for (let i = 1; i < parsed.length; i += 1) {
    const line = parsed[i] ?? [];
    const authorEmail = (line[authorIndex] ?? '').trim().toLowerCase();
    const postContent = (line[contentIndex] ?? '').trim();
    if (!authorEmail || !authorEmail.includes('@')) {
      errors.push({ row: i + 1, message: 'Valid author email is required' });
      continue;
    }
    if (!postContent) {
      errors.push({ row: i + 1, message: 'Post content is required' });
      continue;
    }
    rows.push({
      authorEmail,
      content: postContent.slice(0, 20_000),
      createdAt: createdAtIndex >= 0 ? line[createdAtIndex]?.trim() || null : null,
    });
  }

  return { ok: true, rows, errors };
}
